import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { IgButton, IgTypography } from "@intelligems/ig-theme";
import {
  AudienceAction,
  AudienceDto,
  CreateAudienceDto,
  ExperienceCategory,
  ExpressionPayloadType,
  ExpressionType,
} from "@intelligems/ig-types";
import { postfixToExpressionList } from "@intelligems/ig-utils";
import { Grid } from "@mui/material";
import { PriceTestNote } from "@src/components/targeting/components/audienceTargeting/utilComponents/PriceTestNote";
import { isAudienceTargetingEnabled } from "@src/features/experiments/utils/validators";
import {
  resetCustomAudienceState,
  selectedAudienceRolloutInfo,
  setAudienceExpression,
} from "@src/store/slices/audiences";

import { ExpressionsBuilder } from "../expressions";

interface CustomExpressionProps {
  audienceState: AudienceDto | CreateAudienceDto;
  isView?: boolean;
  queryParamIndex: number;
  isRolloutRender?: boolean;
  onTempExpressionUpdate?: (
    expressions: ExpressionPayloadType[],
    queryParamIndex: number,
    expressionType?: ExpressionType,
    action?: AudienceAction
  ) => void;
  experienceCategory: ExperienceCategory;
}

export const CustomExpression = ({
  audienceState,
  queryParamIndex,
  onTempExpressionUpdate,
  isView,
  isRolloutRender,
  experienceCategory,
}: CustomExpressionProps) => {
  const dispatch = useDispatch();
  const rolloutInfo = useSelector(selectedAudienceRolloutInfo);
  const [expressionsList, setExpressionsList] = useImmer<
    ExpressionPayloadType[]
  >(
    audienceState?.filters[queryParamIndex]?.expression
      ? postfixToExpressionList(
          audienceState.filters[queryParamIndex].expression
        )
      : []
  );

  // update rollouts dialog - temp until we move this to redux fully
  useEffect(() => {
    if (rolloutInfo) {
      setExpressionsList(
        audienceState?.filters[queryParamIndex]?.expression
          ? postfixToExpressionList(
              audienceState.filters[queryParamIndex].expression
            )
          : []
      );
    }
  }, [rolloutInfo?.sourcesUsed]);

  useEffect(() => {
    const noEmptyQueries = expressionsList.filter(
      (expression) => expression.queries.length
    );

    // Only dispatch if there are actual expressions configured by the user,
    // or if there was a previous filter that needs to be cleared
    const hasExistingFilter =
      (audienceState.filters[queryParamIndex]?.expression?.length || 0) > 0;
    if (noEmptyQueries.length === 0 && !hasExistingFilter) {
      return;
    }

    const action =
      audienceState.filters[queryParamIndex]?.action ??
      (experienceCategory === "personalization"
        ? "experienceInclude"
        : "randomVariation");
    if (onTempExpressionUpdate) {
      onTempExpressionUpdate(noEmptyQueries, queryParamIndex, "custom", action);
    } else {
      dispatch(
        setAudienceExpression({
          expressionsList: noEmptyQueries,
          filterIndex: queryParamIndex,
          expressionType: "custom",
          action: action,
          isPersonalization: experienceCategory === "personalization",
        })
      );
    }
  }, [expressionsList]);

  useEffect(() => {
    // set state when targeting is cleared
    if (!audienceState.filters.length) {
      setExpressionsList([]);
    }
  }, [audienceState.filters]);

  return (
    <Grid item container rowSpacing={2} direction={"column"}>
      <PriceTestNote />
      <Grid item>
        <IgTypography sx={{ fontWeight: 400 }}>
          {isRolloutRender
            ? "Roll out to visitors matching all of the following conditions."
            : "Visitors matching the following set of conditions"}
        </IgTypography>
        {isRolloutRender && isAudienceTargetingEnabled(audienceState) && (
          <IgButton
            onClick={() => {
              dispatch(resetCustomAudienceState());
            }}
          >
            Clear all
          </IgButton>
        )}
      </Grid>
      <ExpressionsBuilder
        expressionsList={expressionsList}
        setExpressionsList={setExpressionsList}
        renderContext="audience"
        isRolloutView={isRolloutRender}
        viewOnly={isView}
      />
    </Grid>
  );
};
